/**
 * DemoteCell — the actions on a LIVE row: move back to the queue, park in
 * Waiting, or eliminate. All three take the element OFF the tracker; only the
 * bank destination differs.
 *
 * Clicking PLANS first (a read), like the promote flow, so the dialog can say
 * what will really happen: normally the freed slot is NOT backfilled, but at
 * the pool's live floor the only honest options are a SWAP — naming the queued
 * path that goes live to cover it — or a refusal. The server re-checks every
 * guard on write, so a dialog left open while the rotation moved on cannot
 * force a demotion that is no longer safe.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useState } from 'react';
import {
  IconEliminate,
  IconMoveToQueue,
  IconMoveToWaiting,
  queueActionButton,
  queueActionGlyph,
} from '../queue-action-style';
import type { DemoteDestination, QueueElement } from '@trafficpulse/shared';

import { ConfirmDialog } from '@/components/ui';
import { getApiErrorMessage } from '@/lib/api-error';
import { cn } from '@/lib/utils';
import type { QueueProvider } from '@/features/trackers/flow-optimizer/queue-provider';

import DemoteDialogBody, { DEMOTE_COPY } from './DemoteDialogBody';
import { useDemotePlan } from './useDemotePlan';

export interface DemoteCellProps {
  element: QueueElement;
  campaignId: string;
  provider: QueueProvider;
  /** `coverFloor` authorises the swap the dialog just named. */
  onDemote: (
    element: QueueElement,
    to: DemoteDestination,
    coverFloor?: boolean,
  ) => void;
  pending: boolean;
}

const GLYPH = 'h-3 w-3 shrink-0';

const DemoteCell: FC<DemoteCellProps> = ({
  element,
  campaignId,
  provider,
  onDemote,
  pending,
}) => {
  const [confirm, setConfirm] = useState<DemoteDestination | null>(null);
  const plan = useDemotePlan(campaignId, provider);
  const request = (to: DemoteDestination) => {
    setConfirm(to);
    plan.mutate({ layer: element.layer, bankId: element.bankId });
  };
  const close = () => {
    setConfirm(null);
    plan.reset();
  };
  const copy = confirm ? DEMOTE_COPY[confirm] : null;
  const needsCover = plan.data?.mode === 'needs-cover';
  const refused = plan.data?.mode === 'blocked' || plan.isError;
  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        onClick={() => request('queue')}
        disabled={pending}
        title="Take off the tracker and put back in the queue"
        className={queueActionButton('queue')}
      >
        <IconMoveToQueue className={cn(GLYPH, queueActionGlyph('queue'))} aria-hidden />
        To queue
      </button>
      <button
        type="button"
        onClick={() => request('waiting')}
        disabled={pending}
        title="Take off the tracker and park in Waiting"
        className={queueActionButton('waiting')}
      >
        <IconMoveToWaiting className={cn(GLYPH, queueActionGlyph('waiting'))} aria-hidden />
        To waiting
      </button>
      <button
        type="button"
        onClick={() => request('eliminated')}
        disabled={pending}
        title="Take off the tracker and eliminate"
        className={queueActionButton('eliminate')}
      >
        <IconEliminate className={cn(GLYPH, queueActionGlyph('eliminate'))} aria-hidden />
        Eliminate
      </button>
      <ConfirmDialog
        open={confirm !== null}
        title={
          needsCover
            ? `Keep at least ${plan.data?.minLive} live`
            : (copy?.title ?? '')
        }
        description={
          <DemoteDialogBody
            itemName={element.itemName || element.itemId}
            to={confirm ?? 'queue'}
            plan={plan.data}
            loading={plan.isPending}
            error={plan.isError ? getApiErrorMessage(plan.error) : null}
          />
        }
        confirmLabel={
          needsCover ? `Swap and ${copy?.cta.toLowerCase()}` : (copy?.cta ?? '')
        }
        destructive={copy?.destructive}
        busy={pending}
        confirmDisabled={plan.isPending || refused}
        onConfirm={() => {
          if (confirm) onDemote(element, confirm, needsCover);
          close();
        }}
        onCancel={close}
      />
    </span>
  );
};

export default DemoteCell;
